import { ComponentType, lazy, LazyExoticComponent } from "react";

// After a deploy the old hashed chunk files are gone; a tab that loaded the
// previous index.html and then navigates to a lazy route gets a
// ChunkLoadError. Without handling, that error lands in the router's
// errorElement (RouteErrorView), whose reset flow clears the cart — a
// destructive answer to a problem a plain reload fixes (the reload fetches
// the fresh index.html/asset-manifest and thus the new chunk URLs).

// One reload attempt per pathname per tab-session. The key is per pathname
// and is deliberately NEVER cleared on success: clearing on success would
// allow a ping-pong loop (chunk A succeeds and re-arms the guard, chunk B
// fails and reloads, forever). With no clearing, reloads are bounded by
// construction at one per pathname per tab.
const RELOAD_KEY_PREFIX = "chunkLoadReloaded:";

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.name === "ChunkLoadError" ||
    // webpack js/css chunk load failures:
    /Loading (CSS )?chunk .* failed/i.test(error.message) ||
    // native dynamic-import failures (Safari/Firefox wording):
    /Failed to fetch dynamically imported module/i.test(error.message) ||
    /Importing a module script failed/i.test(error.message)
  );
}

/**
 * Initiates the one guarded reload for the current pathname. Returns true if
 * the reload was started (the page is about to go away), false if this
 * pathname already used its attempt — or storage is unavailable, in which
 * case reloading would be ungated and is therefore skipped.
 */
export function attemptChunkReload(): boolean {
  const key = RELOAD_KEY_PREFIX + window.location.pathname;
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * Drop-in replacement for React.lazy: on a chunk-load failure it performs the
 * one guarded reload above instead of surfacing the error. If the guard is
 * already spent (or the error is not chunk-related), the error propagates to
 * the router's errorElement exactly like plain React.lazy.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own signature
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (isChunkLoadError(error) && attemptChunkReload()) {
        // The page is reloading: never settle, so Suspense keeps showing the
        // loading fallback instead of flashing an error view on the way out.
        return new Promise<never>(() => {});
      }
      throw error;
    }
  });
}
